import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { styled } from '@linaria/react'
import type { AddDollarPrefix } from '@/utils/typeUtilites'
import { colors, media } from '@/lib/theme'
import { headingStyleMarketingVariants } from '@/UI/typography-marketing'
import { Styleable } from '@/lib/interfaces'
import { pxToRem } from '@/utils/unitsInRem'
import { Indicator } from './Indicator'
import { makeTransitionStyles } from '@/lib/sharedStyles'
import { ACCORDION_CLOSE_EVENT } from '@/UI/accordion/utils'

type AppearanceType = 'light' | 'dark'
type StylesType = {
  color: string
  backgroundColor: string
  actionColor: string
}

const appearancePresets: Record<AppearanceType, StylesType> = {
  light: {
    color: colors.textStyles.regular,
    backgroundColor: colors.background.grayLightest,
    actionColor: colors.primary.lightest,
  },
  dark: {
    color: colors.white,
    backgroundColor: colors.secondary.lightest,
    actionColor: colors.tertiary.darkest,
  },
}

interface Props extends Styleable {
  isOpenByDefault?: boolean
  groupId?: string
  title: ReactNode
  content: ReactNode | ReactNode[]
  appearance?: AppearanceType
  contentStyle?: string
}

export const Accordion = ({
                            isOpenByDefault,
                            className,
                            title,
                            content,
                            groupId = '',
                            appearance = 'light',
                            contentStyle,
                          }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const { color, backgroundColor, actionColor } = appearancePresets[appearance]

  const [isOpened, setIsOpened] = useState(isOpenByDefault)
  const toggleOpen = () => setIsOpened(oldState => !oldState)
  const [height, setHeight] = useState(!isOpenByDefault ? '0px' : 'auto')

  useEffect(() => {
    if (isOpened && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`)
    } else {
      setHeight('0px')
    }
  }, [isOpened, content])

  useEffect(() => {
    const handler = (event: Event) => {
      // @ts-ignore
      if (event.detail?.groupId === groupId) setIsOpened(false)
    }
    window.addEventListener(ACCORDION_CLOSE_EVENT, handler)
    return () => window.removeEventListener(ACCORDION_CLOSE_EVENT, handler)
  }, [groupId])

  return (
    <AccordionWrapper
      onClick={toggleOpen}
      className={className}
      $color={color}
      $backgroundColor={backgroundColor}
      $actionColor={actionColor}
    >
      <TitleSection>
        <Indicator isOn={isOpened} strokeColor={color} />
        {title}
      </TitleSection>
      <ContentSection ref={contentRef} $isVisible={isOpened} $contentHeight={height}>
        <ContentInner className={contentStyle}>{content}</ContentInner>
      </ContentSection>
    </AccordionWrapper>
  )
}

const AccordionWrapper = styled.div<AddDollarPrefix<StylesType>>`
  display: grid;
  padding: 1.5rem;
  border-radius: 1rem;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $color }) => $color};
  user-select: none;
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.3s ease-in-out;

  ${media.hover} {
    &:hover {
      background-color: ${({ $actionColor }) => $actionColor};

      & .indicator {
        stroke: ${colors.primary.primary};
      }
    }
  }
  &:active {
    background-color: ${({ $actionColor }) => $actionColor};

    & .indicator {
      stroke: ${colors.primary.primary};
    }
  }
`

const TitleSection = styled.div`
  position: relative;
  padding-left: 2rem;
  padding-top: ${pxToRem(4)};
  ${headingStyleMarketingVariants.sm}
`

const ContentSection = styled.div<{ $isVisible: boolean; $contentHeight: string }>`
  overflow: hidden;
  max-height: ${({ $contentHeight }) => $contentHeight};
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  ${makeTransitionStyles({ transitionProperty: 'max-height, opacity', transitionDuration: '0.3s' })};
`

const ContentInner = styled.div`
  padding-top: 1rem;
`
